<template>
  <div id="new-board" class="new-board">
    <h2 class="page-heading">New Board</h2>
    <form @submit.prevent="saveBoard" @keydown.enter.prevent>
      <div class="form-row-wrapper">
        <div class="form-row">
          <label for="boardId">Board ID <span class="required-asterisk" aria-hidden="true" title="Required">*</span></label>
          <input id="boardId" type="text" v-model="boardId" required readonly />
        </div>
        <div class="form-row">
          <label for="name">Name <span class="required-asterisk" aria-hidden="true" title="Required">*</span></label>
          <input id="name" type="text" @input="onNameInput" v-model="name" required />
        </div>
        <div class="form-row">
          <label for="slug">Slug <span class="required-asterisk" aria-hidden="true" title="Required">*</span></label>
          <input id="slug" type="text" v-model="slug" required readonly />
        </div>
        <div class="form-row">
          <label for="promptPrefix">Prompt prefix</label>
          <input id="promptPrefix" type="text" v-model="promptPrefix" />
        </div>
        <div class="form-row select">
          <label for="category">Category <span class="required-asterisk" aria-hidden="true" title="Required">*</span></label>
          <select id="category" v-model="category" required>
            <option v-for="category in categorySelectData" :value="category.CONSTANT" :key="category.CONSTANT">
              {{ category.DISPLAY_NAME }}
            </option>
          </select>
          <div class="select__arrow"></div>
        </div>
        <div class="form-row select">
          <label for="mode">Mode <span class="required-asterisk" aria-hidden="true" title="Required">*</span></label>
          <select id="mode" v-model="mode" required>
            <option v-for="boardMode in boardModeSelectData" :value="boardMode.value" :key="boardMode.value">
              {{ boardMode.label }}
            </option>
          </select>
          <div class="select__arrow"></div>
        </div>
        <div class="form-row">
          <label for="imagesFolderName">Images folder name <span class="required-asterisk" aria-hidden="true" title="Required">*</span></label>
          <input id="imagesFolderName" type="text" @input="onImagesFolderNameInput" v-model="imagesFolderName" required />
        </div>
        <div class="form-row">
          <label for="keywordsCommaSeparated">Keywords <span class="keywords-subtext">(comma separated)</span></label>
          <input id="keywordsCommaSeparated" type="text" v-model="keywordsCommaSeparated" @blur="onKeywordsBlur" />
        </div>
        <div class="form-row select">
          <label for="boardPortraitLayout">Portrait layout <span class="required-asterisk" aria-hidden="true" title="Required">*</span></label>
          <select id="boardPortraitLayout" v-model="boardPortraitLayout" required>
            <option v-for="layout in boardPortraitLayoutSelectData" :value="layout.value" :key="layout.value">
              {{ layout.label }}
            </option>
          </select>
          <div class="select__arrow"></div>
        </div>
        <div class="form-row select">
          <label for="tileBackgroundSize">Tile background size <span class="required-asterisk" aria-hidden="true" title="Required">*</span></label>
          <select id="tileBackgroundSize" v-model="tileBackgroundSize" required>
            <option v-for="size in tileBackgroundSizesSelectData" :value="size.value" :key="size.value">
              {{ size.label }}
            </option>
          </select>
          <div class="select__arrow"></div>
        </div>
        <div class="form-row select">
          <label for="tileBackgroundPosition">Tile background position <span class="required-asterisk" aria-hidden="true" title="Required">*</span></label>
          <select id="tileBackgroundPosition" v-model="tileBackgroundPosition" required>
            <option v-for="position in tileBackgroundPositionsSelectData" :value="position.value" :key="position.value">
              {{ position.label }}
            </option>
          </select>
          <div class="select__arrow"></div>
        </div>
        <div class="form-row">
          <label class="control control--checkbox"
            >Tile padding
            <input type="checkbox" v-model="tilePadding" />
            <div class="control__indicator"></div>
          </label>
        </div>
        <div class="form-row">
          <label class="control control--checkbox"
            >Invert tile images in Dark Mode
            <input type="checkbox" v-model="invertTileImagesInDarkMode" />
            <div class="control__indicator"></div>
          </label>
        </div>
        <div class="form-row">
          <label for="tileBackgroundColor">Tile background colour</label>
          <div class="form-row__inner-row">
            <input id="tileBackgroundColor" type="text" v-model="tileBackgroundColor" />
            <label for="tileBackgroundColorPicker" class="color-picker__label" :style="backgroundColorPickerLabelInlineStyle"><span class="sr-only">Colour picker</span></label>
            <input id="tileBackgroundColorPicker" type="color" v-model="tileBackgroundColor" class="color-picker__input sr-only" />
          </div>
        </div>
        <div class="form-row">
          <label for="tileRadialGradientHexColor">Tile radial gradient hex colour</label>
          <div class="form-row__inner-row">
            <input id="tileRadialGradientHexColor" type="text" v-model="tileRadialGradientHexColor" />
            <label for="tileRadialGradientHexColorPicker" class="color-picker__label" :style="radialGradientColorPickerLabelInlineStyle"
              ><span class="sr-only">Colour picker</span></label
            >
            <input id="tileRadialGradientHexColorPicker" type="color" v-model="tileRadialGradientHexColor" class="color-picker__input sr-only" />
          </div>
        </div>
        <div class="form-row select">
          <label for="tileImageFormat">Tile image format <span class="required-asterisk" aria-hidden="true" title="Required">*</span></label>
          <select id="tileImageFormat" v-model="tileImageFormat" @change="onTileImageFormatChange" required>
            <option v-for="format in tileImageFormatSelectData" :value="format.value" :key="format.value">
              {{ format.label }}
            </option>
          </select>
          <div class="select__arrow"></div>
        </div>
      </div>
      <div class="tiles__wrapper">
        <p class="images-drop-zone" :class="`${dragOver ? 'images-drop-zone--over' : ''}`" @drop="onImagesDrop" @dragover="onImagesDragOver" @dragleave="onImagesDragLeave">
          Drag-and-drop tile images here to create tiles!
        </p>
        <button type="button" class="tiles__rename-tsv-btn" @click="onRenameTsvBtnClick">Generate 'Rename TSV' file</button>
        <div class="tiles__header">
          <h3 class="tiles__heading">Tiles ({{ numTiles }})</h3>
          <button type="button" @click="onAddNewTileBtnClick('TOP')" class="new-tile-btn">Add new tile</button>
        </div>
        <div v-if="tiles.length > 1" class="form-row select form-row--order-tiles-by">
          <div class="form-row__child">
            <label for="orderTilesBy">Order tiles by</label>
            <select name="orderTilesBy" id="orderTilesBy" v-model="orderTilesBy">
              <option v-for="item in newTilesOrderBySelectData" :value="item.value" :key="item.value">
                {{ item.label }}
              </option>
            </select>
            <div class="select__arrow"></div>
          </div>
        </div>
        <ul class="tile-list style-none">
          <li v-for="(tile, index) in orderedTiles" :key="tile.tileId" class="tile-list__item">
            <EditBoardTile :tileObject="tiles[index]" :index="index" @delete-tile="deleteTile" :ref="`tile${index}`" />
          </li>
        </ul>
        <div v-if="tiles.length > 0" class="tiles__footer">
          <h3 class="tiles__heading">Tiles ({{ numTiles }})</h3>
          <button type="button" @click="onAddNewTileBtnClick('BOTTOM')" class="new-tile-btn">Add new tile</button>
        </div>
      </div>
      <div class="form-footer">
        <p v-if="slugHasAlreadyBeenUsed" class="warning-message">This slug has been used before, please change the name so the slug is unique.</p>
        <p v-if="slugIsTheSameAsId" class="warning-message">The name/slug cannot be the same as the Board ID.</p>
        <p v-if="slugIsTheSameAsExistingBoardId" class="warning-message">The name/slug cannot be the same as an existing Board ID.</p>
        <button type="submit">Create New Board</button>
        <button type="button" @click="onCancel" class="button button--as-link cancel-btn">Cancel</button>
      </div>
    </form>
  </div>
</template>

<script>
import firebase from 'firebase/app';
import kebabCase from 'lodash/kebabCase';
import uniq from 'lodash/uniq';
import sortBy from 'lodash/sortBy';
import {saveAs} from 'file-saver';
import arrayToCsv from './../helpers/arrayToCsv';
import capitalize from './../helpers/capitalize';
import csvToArray from './../helpers/csvToArray';
import db from './../firebaseInit';
import EditBoardTile from './../components/EditBoardTile';
import filenameToPromptString from './../helpers/filenameToPromptString';
import generateNewBoardId from './../helpers/generateNewBoardId';
import generatePassword from './../helpers/generatePassword';
import getFormattedTimestamp from './../helpers/getFormattedTimestamp';
import {
  BOARD_MODES_SELECT_DATA,
  BOARD_MODES,
  BOARD_PORTRAIT_LAYOUTS_SELECT_DATA,
  BOARD_PORTRAIT_LAYOUTS,
  CATEGORIES,
  DEFAULT_BOARD_MODE,
  DEFAULT_BOARD_PORTRAIT_LAYOUT,
  DEFAULT_PROMOTED,
  DEFAULT_TILE_BACKGROUND_COLOR,
  DEFAULT_TILE_BACKGROUND_POSITION,
  DEFAULT_TILE_BACKGROUND_SIZE,
  DEFAULT_TILE_IMAGE_FORMAT,
  DEFAULT_TILE_RADIAL_GRADIENT_HEX_COLOR,
  GENERATE_FIREBASE_DOCUMENT_ID_CHARS_POOL,
  GENERATE_FIREBASE_DOCUMENT_ID_LENGTH,
  IMAGES_FOLDER_NAME_DELIMITER,
  NEW_TILES_ORDER_BY,
  NEW_TILES_ORDER_BY_SELECT_DATA,
  NOTIFICATION_SETTINGS,
  TILE_BACKGROUND_POSITIONS_SELECT_DATA,
  TILE_BACKGROUND_POSITIONS,
  TILE_BACKGROUND_SIZES_SELECT_DATA,
  TILE_BACKGROUND_SIZES,
  TILE_IMAGE_FORMATS,
  TILE_IMAGE_FORMATS_SELECT_DATA,
} from './../constants/globalConstants';

export default {
  name: 'new-board',
  title: 'New Board',
  components: {
    EditBoardTile,
  },
  data() {
    return {
      boardId: '',
      boardPortraitLayout: DEFAULT_BOARD_PORTRAIT_LAYOUT,
      category: '',
      dragOver: false,
      existingBoardIds: [],
      existingSlugs: [],
      imagesFolderName: '',
      invertTileImagesInDarkMode: false,
      keywords: [],
      keywordsCommaSeparated: '',
      mode: DEFAULT_BOARD_MODE,
      name: '',
      numOriginalImageFilenames: 0,
      orderTilesBy: NEW_TILES_ORDER_BY.CREATION.CONSTANT,
      promoted: DEFAULT_PROMOTED,
      promptPrefix: '',
      slug: '',
      slugHasAlreadyBeenUsed: false,
      slugIsTheSameAsId: false,
      slugIsTheSameAsExistingBoardId: false,
      tileImageFormat: DEFAULT_TILE_IMAGE_FORMAT,
      tileBackgroundColor: DEFAULT_TILE_BACKGROUND_COLOR,
      tileBackgroundPosition: DEFAULT_TILE_BACKGROUND_POSITION,
      tileBackgroundSize: DEFAULT_TILE_BACKGROUND_SIZE,
      tilePadding: false,
      tileRadialGradientHexColor: DEFAULT_TILE_RADIAL_GRADIENT_HEX_COLOR,
      tiles: [],
      userHasManuallyEnteredImagesFolderName: false,
    };
  },
  computed: {
    addImagesFolderNameActiveClass: function() {
      return {
        'force-active': this.imagesFolderName !== null && this.imagesFolderName !== '',
      };
    },
    boardModeSelectData: function() {
      return BOARD_MODES_SELECT_DATA;
    },
    boardPortraitLayoutSelectData: function() {
      return BOARD_PORTRAIT_LAYOUTS_SELECT_DATA;
    },
    newTilesOrderBySelectData: function() {
      return NEW_TILES_ORDER_BY_SELECT_DATA;
    },
    tileBackgroundPositionsSelectData: function() {
      return TILE_BACKGROUND_POSITIONS_SELECT_DATA;
    },
    tileBackgroundSizesSelectData: function() {
      return TILE_BACKGROUND_SIZES_SELECT_DATA;
    },
    tileImageFormatSelectData: function() {
      return TILE_IMAGE_FORMATS_SELECT_DATA;
    },
    categorySelectData: function() {
      return sortBy(CATEGORIES, 'DISPLAY_NAME');
    },
    backgroundColorPickerLabelInlineStyle: function() {
      if (this.tileBackgroundColor) {
        return `background-color: ${this.tileBackgroundColor}; border: 0;`;
      } else {
        return '';
      }
    },
    radialGradientColorPickerLabelInlineStyle: function() {
      if (this.tileRadialGradientHexColor) {
        return `background-color: ${this.tileRadialGradientHexColor}; border: 0;`;
      } else {
        return '';
      }
    },
    tileRadialGradientHexColorPicker: function() {
      if (this.tileRadialGradientHexColor !== '') {
        return this.tileRadialGradientHexColor;
      } else {
        return '#000000';
      }
    },
    numTiles: function() {
      return this.tiles.length;
    },
    orderedTiles: function() {
      let tempArray = [];
      switch (this.orderTilesBy) {
        case NEW_TILES_ORDER_BY.CREATION.CONSTANT:
          tempArray = sortBy(this.tiles, NEW_TILES_ORDER_BY.CREATION.PROPERTY);
          break;
        case NEW_TILES_ORDER_BY.ID.CONSTANT:
          tempArray = sortBy(this.tiles, NEW_TILES_ORDER_BY.ID.PROPERTY);
          break;
        case NEW_TILES_ORDER_BY.PROMPT.CONSTANT:
          tempArray = sortBy(this.tiles, NEW_TILES_ORDER_BY.PROMPT.PROPERTY);
          break;
        default:
          tempArray = this.tiles;
      }
      return tempArray;
    },
    // numOriginalImageFilenames: function() {
    //   const originalImageFilenames = this.tiles.filter(tile => {
    //     tile.originalImageFilename !== '';
    //   });
    //   return originalImageFilenames.length;
    // },
  },
  created() {
    window.scrollTo(0, 0);
    db.collection('boards')
      .orderBy('boardId')
      .get()
      .then(snapshot => {
        snapshot.forEach(doc => {
          this.$data.existingBoardIds.push(doc.data().boardId);
          this.$data.existingSlugs.push(doc.data().slug);
        });
        this.$data.existingSlugs = this.$data.existingSlugs.filter(slug => slug !== '');
        this.$data.boardId = generateNewBoardId(this.$data.existingBoardIds);
      });
  },
  methods: {
    onNameInput() {
      this.name = capitalize(this.name);
      if (this.imagesFolderName === '') {
        this.userHasManuallyEnteredImagesFolderName = false;
      }
      if (this.boardId && !this.userHasManuallyEnteredImagesFolderName) {
        this.imagesFolderName = `${this.boardId}${IMAGES_FOLDER_NAME_DELIMITER}${kebabCase(this.name)}`;
      }
      this.slug = kebabCase(this.name);
    },
    onImagesFolderNameInput() {
      this.userHasManuallyEnteredImagesFolderName = true;
    },
    onKeywordsBlur() {
      this.keywords = uniq(csvToArray(this.keywordsCommaSeparated.toLowerCase()));
      this.keywordsCommaSeparated = arrayToCsv(this.keywords);
    },
    onImagesDrop: function(evt) {
      evt.preventDefault();
      this.dragOver = false;
      evt.dataTransfer.items.forEach(item => {
        const filename = item.getAsFile().name;
        this.addNewTile(filename, filenameToPromptString(filename));
      });
      this.updateNumOriginalImageFilenames();
    },
    onImagesDragOver: function(evt) {
      evt.preventDefault();
      this.dragOver = true;
    },
    onImagesDragLeave: function(evt) {
      evt.preventDefault();
      this.dragOver = false;
    },
    onAddNewTileBtnClick(btnLocation) {
      this.addNewTile('', '');
      if (btnLocation === 'BOTTOM') {
        setTimeout(function() {
          window.scrollTo({
            left: 0,
            top: document.body.scrollHeight,
            behavior: 'auto',
          });
        }, 50);
      }
    },
    onRenameTsvBtnClick() {
      let renameTsv = ``;
      const tilesWithOriginalImageFilenames = this.tiles.filter(tile => tile.originalImageFilename !== '');
      this.tiles.forEach((tile, index) => {
        if (tile.originalImageFilename !== '') {
          const newline = index < tilesWithOriginalImageFilenames.length - 1 ? '\n' : '';
          renameTsv += `${tile.originalImageFilename}\t${tile.imageFilename}${newline}`;
        }
      });
      const blob = new Blob([renameTsv], {type: 'text/plain;charset=utf-8'});
      window.saveAs(blob, `rename_images_${getFormattedTimestamp()}.tsv`);
    },
    addNewTile(originalImageFilename, prompt) {
      if (typeof originalImageFilename !== 'string') originalImageFilename = '';
      if (typeof prompt !== 'string') prompt = '';
      const tileId = generatePassword(GENERATE_FIREBASE_DOCUMENT_ID_LENGTH, GENERATE_FIREBASE_DOCUMENT_ID_CHARS_POOL);
      this.tiles.push({
        answerText: '',
        backgroundColor: '',
        enabled: true,
        imageFilename: `${tileId}${TILE_IMAGE_FORMATS[this.tileImageFormat].EXTENSION}`,
        originalImageFilename: originalImageFilename,
        prompt: prompt,
        radialGradientHexColor: '',
        tileId: tileId,
        timestamp: Date.now(),
      });
    },
    onTileImageFormatChange() {
      this.tiles.forEach((tile, index) => {
        this.$refs[`tile${index}`][0].extensionChanged(TILE_IMAGE_FORMATS[this.tileImageFormat].EXTENSION);
      });
    },
    deleteTile(index) {
      this.$delete(this.tiles, index);
      this.updateNumOriginalImageFilenames();
    },
    updateNumOriginalImageFilenames() {
      this.numOriginalImageFilenames = this.tiles.filter(tile => tile.originalImageFilename !== '').length;
    },
    saveBoard() {
      let isError = false;

      if (this.existingSlugs.includes(this.slug)) {
        this.slugHasAlreadyBeenUsed = true;
        isError = true;
      } else {
        this.slugHasAlreadyBeenUsed = false;
      }

      if (this.slug.trim() === this.boardId.trim()) {
        this.slugIsTheSameAsId = true;
        isError = true;
      } else {
        this.slugIsTheSameAsId = false;
        if (this.existingBoardIds.includes(this.slug)) {
          this.slugIsTheSameAsExistingBoardId = true;
          isError = true;
        } else {
          this.slugIsTheSameAsExistingBoardId = false;
        }
      }

      if (!isError) {
        db.collection('boards')
          .doc(this.boardId)
          .set({
            boardCreatedAt: firebase.firestore.FieldValue.serverTimestamp(),
            boardId: this.boardId,
            boardPortraitLayout: this.boardPortraitLayout,
            category: this.category,
            imagesFolderName: this.imagesFolderName,
            invertTileImagesInDarkMode: this.invertTileImagesInDarkMode,
            keywords: this.keywords,
            mode: this.mode,
            name: this.name,
            promoted: this.promoted,
            promptPrefix: this.promptPrefix,
            slug: this.slug,
            tileBackgroundColor: this.tileBackgroundColor,
            tileBackgroundPosition: this.tileBackgroundPosition,
            tileBackgroundSize: this.tileBackgroundSize,
            tilePadding: this.tilePadding,
            tileRadialGradientHexColor: this.tileRadialGradientHexColor,
          })
          .then(() => {
            const tilesBatch = db.batch();
            this.tiles.forEach(tile => {
              const tileRef = db
                .collection('boards')
                .doc(this.boardId)
                .collection('tiles')
                .doc(tile.tileId);
              tilesBatch.set(tileRef, tile);
            });
            tilesBatch
              .commit()
              .then(() => {
                this.$snotify.success('New board created', '', NOTIFICATION_SETTINGS).on('destroyed', () => {
                  this.submittingForm = false;
                  this.$router.push('/boards');
                });
              })
              .catch(error => {
                this.$snotify.error('Error creating new tiles', '', NOTIFICATION_SETTINGS);
              });
          })
          .catch(error => {
            this.$snotify.error('Error saving new board', '', NOTIFICATION_SETTINGS);
          });
      }
    },
    onCancel() {
      this.$router.go(-1);
    },
  },
};
</script>

<style lang="scss" scoped>
.force-active {
  transform: translateY(-14px) scale(0.8);
  transform-origin: 0 0;
}

.cancel-btn {
  margin-top: 2rem;
}

.form-row__inner-row {
  display: flex;
  flex-direction: row;
  width: 100%;
}

.color-picker__label {
  align-items: center;
  border: 1px solid var(--color-picker-label-border);
  border-radius: var(--border-radius);
  cursor: pointer;
  display: flex;
  flex-basis: 0;
  justify-content: center;
  margin-bottom: 0;
  margin-left: 1rem;
  min-width: 5rem;
  width: 5rem;
}

.images-drop-zone {
  border: 1px dashed var(--text-color-alpha-30);
  border-radius: var(--border-radius);
  color: var(--text-color-alpha-30);
  font-size: 1.4rem;
  font-weight: 300;
  margin-bottom: 3rem;
  margin-top: 0;
  padding: 3rem 0.7rem;
  max-width: none;
  text-align: center;

  &--over {
    color: var(--text-color);
    border-color: var(--text-color);
  }
}

.tiles {
  &__wrapper {
    margin-bottom: 3rem;
    max-width: var(--max-width-form);
    width: 100%;
  }

  &__rename-tsv-btn {
    margin-bottom: 2rem;
    width: 100%;
  }

  &__header {
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin-bottom: 2rem;
  }

  &__footer {
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin-top: 2rem;
  }

  &__heading {
    margin: 0;
  }
}

.tile-list {
  &__item {
    margin-bottom: 2rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.form-footer {
  display: flex;
  flex-direction: column;
}

.form-row--order-tiles-by {
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin: 0 auto 2rem auto;
  max-width: var(--max-width-main-content);

  label {
    flex-shrink: 0;
    margin-right: 1rem;
  }
  select {
    max-width: 20rem;
  }
}

.form-row__child {
  align-items: center;
  display: flex;
  position: relative;
}
</style>
